import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import callerService from '../../services/callerService';
import resultsService from '../../services/resultsService';
import resultsStreamService from '../../services/resultsStreamService';
import { groupsService } from '../../services';
import TailwindPageWrapper from "../generic/TailwindPageWrapper";
import GenericSelectTailwind from "../admin/GenericSelectTailwind";

const CallerView = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Venue des Callers - wenn keine zugewiesen, kann der User wählen
  const assignedVenueFromUser = user?.venue;
  const [selectedVenue, setSelectedVenue] = useState(assignedVenueFromUser || "");
  const assignedVenue = assignedVenueFromUser || selectedVenue;

  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [preparationEvents, setPreparationEvents] = useState([]);
  const [preparationEventsWithParticipants, setPreparationEventsWithParticipants] = useState([]);
  const [checklist, setChecklist] = useState(new Map()); // Map von groupId_participantId -> checked
  const [checklistData, setChecklistData] = useState(new Map()); // Backend-Daten: groupId -> Map(participantId -> data)
  const [partnerTeamsData, setPartnerTeamsData] = useState(new Map()); // Partner-Teams-Daten pro Gruppe (Map von groupId -> { partnerTeams: [...] })

  // Neue Caller-Optimierungen
  const [searchTerm, setSearchTerm] = useState(""); // Suchfeld für Namen/Startnummern
  const [showOnlyMissing, setShowOnlyMissing] = useState(false); // Nur fehlende Teilnehmer anzeigen
  const [showConfirmAllModal, setShowConfirmAllModal] = useState(false); // Bestätigungsdialog für "Alle anwesend"
  const [confirmAllGroupId, setConfirmAllGroupId] = useState(null); // Gruppe für "Alle anwesend"
  const [confirmAllGroupName, setConfirmAllGroupName] = useState(""); // Gruppenname für Modal

  // Ergebnis-Sync mit Scorekeeper
  const [groupResults, setGroupResults] = useState(new Map()); // participantId/teamId -> true (hat Ergebnis)
  const previousGroupIdRef = useRef(null);

  useEffect(() => {
    if (assignedVenue) {
      setLoading(true);
      loadData();
      loadChecklistFromStorage();

      // Auto-Refresh alle 30 Sekunden
      const interval = setInterval(() => {
        loadData();
      }, 30000);

      return () => clearInterval(interval);
    } else {
      // Wenn keine venue ausgewählt ist, setze loading auf false
      setLoading(false);
    }
  }, [assignedVenue]);

  // SSE-Verbindung für Real-Time Ergebnis-Updates der aktiven Gruppe
  useEffect(() => {
    const groupId = currentEvent?.group?.id;

    // Wenn Gruppe wechselt, alte Verbindung trennen
    if (previousGroupIdRef.current && previousGroupIdRef.current !== groupId) {
      resultsStreamService.disconnectGroup(previousGroupIdRef.current);
    }
    previousGroupIdRef.current = groupId;

    if (!groupId) return;

    // Lade initiale Ergebnisse für aktive Gruppe
    const loadResults = async () => {
      try {
        const response = await resultsService.getResultsByGroup(groupId);
        if (response.success && Array.isArray(response.data)) {
          const resultsMap = new Map();
          response.data.forEach(result => {
            // Unterstütze sowohl participantId als auch partnerTeamId
            const key = result.partnerTeamId
              ? `partner-team-${result.partnerTeamId}`
              : result.participantId;
            if (key) resultsMap.set(key, true);
          });
          setGroupResults(resultsMap);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Gruppenergebnisse:', error);
      }
    };

    loadResults();

    // SSE für Real-Time Updates
    resultsStreamService.connect(groupId, (update) => {
      if (update.type === 'result-update' && update.result) {
        const result = update.result;
        const key = result.partnerTeamId
          ? `partner-team-${result.partnerTeamId}`
          : result.participantId;
        if (key) {
          setGroupResults(prev => {
            const newMap = new Map(prev);
            newMap.set(key, true);
            return newMap;
          });
        }
      }
    });

    return () => {
      if (groupId) {
        resultsStreamService.disconnectGroup(groupId);
      }
    };
  }, [currentEvent?.group?.id]);

  // Hilfsfunktion: Prüft ob Teilnehmer/Team bereits Ergebnis hat
  const hasResult = (participantId, isPartnerTeam = false) => {
    const key = isPartnerTeam ? `partner-team-${participantId}` : participantId;
    return groupResults.has(key);
  };

  // Lade Checkliste vom Backend für alle Vorbereitungsgruppen
  useEffect(() => {
    const loadChecklistsFromBackend = async () => {
      if (preparationEventsWithParticipants.length === 0) return;

      const newChecklistData = new Map();

      for (const event of preparationEventsWithParticipants) {
        if (event.group?.id) {
          try {
            const result = await callerService.getChecklist(event.group.id);
            if (result.success && result.data) {
              const groupChecklist = new Map();
              result.data.forEach(item => {
                if (item.partnerTeamId) {
                  // Partner Team
                  const key = `partner-team-${item.partnerTeamId}`;
                  groupChecklist.set(key, item);
                } else if (item.participantId) {
                  // Normaler Teilnehmer
                  groupChecklist.set(item.participantId, item);
                }
              });
              newChecklistData.set(event.group.id, groupChecklist);
            }
          } catch (error) {
            console.error(`Fehler beim Laden der Checkliste für Gruppe ${event.group.id}:`, error);
          }
        }
      }

      // Merge mit bestehenden Daten (behalte aktive Gruppe falls vorhanden)
      setChecklistData(prev => {
        const merged = new Map(prev);
        // Überschreibe nur Vorbereitungsgruppen (nicht aktive Gruppe)
        // Die aktive Gruppe wird separat in loadData geladen und sollte nicht überschrieben werden
        newChecklistData.forEach((value, key) => {
          // Überschreibe nur wenn die Gruppe nicht bereits in prev vorhanden ist
          // oder wenn sie definitiv eine Vorbereitungsgruppe ist (Status !== 'Aktiv')
          const event = preparationEventsWithParticipants.find(e => e.group?.id === key);
          if (event && event.status !== 'Aktiv') {
            merged.set(key, value);
          } else if (!prev.has(key)) {
            // Wenn die Gruppe noch nicht geladen wurde, lade sie
            merged.set(key, value);
          }
          // Ansonsten behalte die bestehenden Daten (für aktive Gruppe)
        });
        return merged;
      });
    };

    loadChecklistsFromBackend();
  }, [preparationEventsWithParticipants]);

  // Lade Checkliste aus Local Storage
  const loadChecklistFromStorage = () => {
    const stored = new Map();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('caller_checklist_')) {
        try {
          const value = JSON.parse(localStorage.getItem(key));
          stored.set(key, value);
        } catch (e) {
          console.error('Fehler beim Laden der Checkliste:', e);
        }
      }
    }
    setChecklist(stored);
  };

  // Speichere Checkliste-Status im Local Storage
  const saveChecklistItem = (groupId, participantId, checked) => {
    const key = `caller_checklist_${groupId}_${participantId}`;
    const value = {
      checked,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(value));

    setChecklist(prev => {
      const newMap = new Map(prev);
      newMap.set(key, value);
      return newMap;
    });
  };

  // Prüfe ob Athlet abgehakt ist (Backend hat Priorität)
  const isParticipantChecked = (groupId, participantId) => {
    // Prüfe zuerst Backend-Daten
    const groupChecklist = checklistData.get(groupId);
    if (groupChecklist) {
      const item = groupChecklist.get(participantId);
      if (item) {
        return item.checkedByCaller || false;
      }
    }

    // Fallback zu Local Storage
    const key = `caller_checklist_${groupId}_${participantId}`;
    const item = checklist.get(key);
    return item ? item.checked : false;
  };

  // Prüfe ob ein Partner-Team abgehakt ist (Team-Level)
  const isPartnerTeamChecked = (groupId, partnerTeam) => {
    if (!partnerTeam || !partnerTeam.id) {
      return false;
    }
    const partnerTeamKey = `partner-team-${partnerTeam.id}`;
    const groupChecklist = checklistData.get(groupId) || new Map();
    const item = groupChecklist.get(partnerTeamKey);
    return item ? item.checkedByCaller : false;
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Lade Schedule-Events für die zugewiesene Fläche
      const scheduleResult = await scheduleService.getAllScheduleEvents({ venue: assignedVenue });
      const scheduleEvents = scheduleResult.success ? scheduleResult.data || [] : [];

      // Sortiere nach Startzeit
      scheduleEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

      // Finde aktuelles Event (Status "Aktiv")
      const activeEvent = scheduleEvents.find(e => e.status === 'Aktiv');

      // Lade Checkliste für aktive Gruppe, falls vorhanden
      if (activeEvent?.group?.id) {
        try {
          const checklistResult = await callerService.getChecklist(activeEvent.group.id);
          if (checklistResult.success && checklistResult.data) {
            const groupChecklist = new Map();
            checklistResult.data.forEach(item => {
              if (item.partnerTeamId) {
                // Partner Team
                const key = `partner-team-${item.partnerTeamId}`;
                groupChecklist.set(key, item);
              } else if (item.participantId) {
                // Normaler Teilnehmer
                groupChecklist.set(item.participantId, item);
              }
            });
            setChecklistData(prev => {
              const newMap = new Map(prev);
              newMap.set(activeEvent.group.id, groupChecklist);
              return newMap;
            });
          }
        } catch (error) {
          console.error('Fehler beim Laden der Checkliste für aktive Gruppe:', error);
        }
      }

      setCurrentEvent(activeEvent || null);

      // Finde alle Events mit Status "Vorbereitung"
      const prepEvents = scheduleEvents.filter(e => e.status === 'Vorbereitung');
      setPreparationEvents(prepEvents);

      // Lade Teilnehmer für Vorbereitungsgruppen und aktive Gruppe
      const eventsToLoad = activeEvent ? [...prepEvents, activeEvent] : prepEvents;

      // Lade Partner-Teams für alle Partnerform-Gruppen
      const partnerFormGroups = eventsToLoad.filter(event =>
        event.group?.category?.type === 'Partnerform' && event.group?.id
      );

      const partnerTeamsMap = new Map();
      if (partnerFormGroups.length > 0) {
        await Promise.all(
          partnerFormGroups.map(async (event) => {
            try {
              const groupResponse = await groupsService.getGroupWithStartOrder(event.group.id);
              if (groupResponse.success && groupResponse.data?.isPartnerForm && groupResponse.data?.partnerTeams) {
                partnerTeamsMap.set(event.group.id, groupResponse.data);
              }
            } catch (error) {
              console.debug(`Fehler beim Laden der Partner-Teams für Gruppe ${event.group.id} (nicht kritisch):`, error);
            }
          })
        );
        setPartnerTeamsData(partnerTeamsMap);
      }

      if (eventsToLoad.length > 0) {
        const participantsResult = await participantsService.getAllParticipants();
        if (participantsResult.success) {
          const allParticipants = participantsResult.data || [];
          const eventsWithParticipants = eventsToLoad.map(event => {
            if (event.group?.id) {
              const isPartnerForm = event.group?.category?.type === 'Partnerform';
              const partnerTeamsForGroup = partnerTeamsMap.get(event.group.id);

              // Für Partnerform-Gruppen: Verwende Partner-Teams statt einzelner Teilnehmer
              if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
                return {
                  ...event,
                  participants: [], // Keine einzelnen Teilnehmer für Partnerform
                  partnerTeams: partnerTeamsForGroup.partnerTeams,
                  participantCount: partnerTeamsForGroup.partnerTeams.length,
                  isPartnerForm: true
                };
              }

              // Normale Gruppen: Filtere einzelne Teilnehmer
              const eventParticipants = allParticipants.filter(p => {
                if (!p.groups || !Array.isArray(p.groups)) return false;
                return p.groups.some(g => g.id === event.group.id);
              });

              // Sortiere nach Startnummer
              eventParticipants.sort((a, b) => {
                const aGroup = a.groups.find(g => g.id === event.group.id);
                const bGroup = b.groups.find(g => g.id === event.group.id);
                const aStartNumber = aGroup ? aGroup.startNumber : 999;
                const bStartNumber = bGroup ? bGroup.startNumber : 999;
                return aStartNumber - bStartNumber;
              });

              return {
                ...event,
                participants: eventParticipants,
                participantCount: eventParticipants.length,
                isPartnerForm: false
              };
            }
            return {
              ...event,
              participants: [],
              participantCount: 0,
              isPartnerForm: false
            };
          });

          // Trenne Vorbereitungs- und aktive Events
          const prepWithParticipants = eventsWithParticipants.filter(e => e.status === 'Vorbereitung');
          setPreparationEventsWithParticipants(prepWithParticipants);

          const activeWithParticipants = eventsWithParticipants.find(e => e.status === 'Aktiv');
          if (activeWithParticipants) {
            setCurrentEvent(activeWithParticipants);
          }
        } else {
          setPreparationEventsWithParticipants(prepEvents.map(e => ({ ...e, participants: [], participantCount: 0, isPartnerForm: false })));
        }
      } else {
        setPreparationEventsWithParticipants([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const handleCheckboxChange = async (groupId, participantId, checked) => {
    // Optimistische UI-Aktualisierung - aktualisiere sofort den lokalen State
    const groupChecklist = checklistData.get(groupId) || new Map();
    const currentItem = groupChecklist.get(participantId) || {};

    // Erstelle optimistisches Update
    const optimisticUpdate = {
      ...currentItem,
      participantId,
      checkedByCaller: checked,
      checkedAt: checked ? (currentItem.checkedAt || new Date().toISOString()) : null,
      isLate: currentItem.isLate || false,
      lateCheckedAt: currentItem.lateCheckedAt || null,
      didNotStart: currentItem.didNotStart || false
    };

    // Aktualisiere lokalen State sofort
    const newChecklistData = new Map(checklistData);
    groupChecklist.set(participantId, optimisticUpdate);
    newChecklistData.set(groupId, groupChecklist);
    setChecklistData(newChecklistData);

    // Auch in Local Storage speichern (für Offline-Support)
    saveChecklistItem(groupId, participantId, checked);

    try {
      // Speichere im Backend (im Hintergrund)
      let result;
      if (checked) {
        result = await callerService.checkParticipant(groupId, participantId);
      } else {
        result = await callerService.uncheckParticipant(groupId, participantId);
      }

      if (!result.success) {
        throw new Error(result.error || 'Fehler beim Aktualisieren der Checkliste');
      }

      // Aktualisiere lokalen State mit Backend-Daten (falls sich etwas geändert hat)
      const backendData = result.data || {};
      const updatedGroupChecklist = newChecklistData.get(groupId) || new Map();
      updatedGroupChecklist.set(participantId, {
        ...updatedGroupChecklist.get(participantId),
        participantId,
        checkedByCaller: checked,
        checkedAt: backendData.checkedAt || (checked ? new Date().toISOString() : null),
        isLate: backendData.isLate || false,
        lateCheckedAt: backendData.lateCheckedAt || null,
        didNotStart: backendData.didNotStart || false
      });

      const finalChecklistData = new Map(newChecklistData);
      finalChecklistData.set(groupId, updatedGroupChecklist);
      setChecklistData(finalChecklistData);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Checkliste:', error);
      // Bei Fehler: Revertiere zu vorherigem Zustand
      const revertedGroupChecklist = checklistData.get(groupId) || new Map();
      const revertedItem = revertedGroupChecklist.get(participantId);
      if (revertedItem) {
        const revertedChecklistData = new Map(checklistData);
        revertedGroupChecklist.set(participantId, revertedItem);
        revertedChecklistData.set(groupId, revertedGroupChecklist);
        setChecklistData(revertedChecklistData);
      }
    }
  };

  // Partner-Team abhaken (Team-Level)
  const handlePartnerTeamCheckboxChange = async (groupId, partnerTeam, checked) => {
    if (!partnerTeam || !partnerTeam.id) {
      return;
    }

    // Optimistische UI-Aktualisierung
    const partnerTeamKey = `partner-team-${partnerTeam.id}`;
    const groupChecklist = checklistData.get(groupId) || new Map();
    const currentItem = groupChecklist.get(partnerTeamKey) || {};

    const optimisticUpdate = {
      ...currentItem,
      partnerTeamId: partnerTeam.id,
      checkedByCaller: checked,
      checkedAt: checked ? (currentItem.checkedAt || new Date().toISOString()) : null,
      didNotStart: !checked
    };

    const newChecklistData = new Map(checklistData);
    groupChecklist.set(partnerTeamKey, optimisticUpdate);
    newChecklistData.set(groupId, groupChecklist);
    setChecklistData(newChecklistData);

    try {
      // Speichere im Backend
      // checked = true bedeutet Team ist abgehakt = anwesend = KEIN DNS
      // checked = false bedeutet Team ist nicht abgehakt = nicht anwesend = DNS
      let result;
      if (checked) {
        // Team abhaken = DNS entfernen
        result = await callerService.checkPartnerTeam(groupId, partnerTeam.id);
      } else {
        // Team nicht abhaken = DNS erstellen
        result = await callerService.uncheckPartnerTeam(groupId, partnerTeam.id);
      }

      if (!result.success) {
        throw new Error(result.error || 'Fehler beim Aktualisieren der Checkliste');
      }

      // Aktualisiere lokalen State mit Backend-Daten
      const backendData = result.data || {};
      const updatedGroupChecklist = newChecklistData.get(groupId) || new Map();
      updatedGroupChecklist.set(partnerTeamKey, {
        ...updatedGroupChecklist.get(partnerTeamKey),
        partnerTeamId: partnerTeam.id,
        checkedByCaller: checked,
        checkedAt: backendData.checkedAt || (checked ? new Date().toISOString() : null),
        didNotStart: !checked
      });

      const finalChecklistData = new Map(newChecklistData);
      finalChecklistData.set(groupId, updatedGroupChecklist);
      setChecklistData(finalChecklistData);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Checkliste:', error);
      // Bei Fehler: Revertiere zu vorherigem Zustand
      const revertedGroupChecklist = checklistData.get(groupId) || new Map();
      const revertedItem = revertedGroupChecklist.get(partnerTeamKey);
      if (revertedItem) {
        const revertedChecklistData = new Map(checklistData);
        revertedGroupChecklist.set(partnerTeamKey, revertedItem);
        revertedChecklistData.set(groupId, revertedGroupChecklist);
        setChecklistData(revertedChecklistData);
      }
    }
  };

  // Berechne Anzahl abgehakter Athleten/Teams für eine Gruppe
  const getCheckedCount = (groupId, participants, partnerTeams, isPartnerForm) => {
    if (isPartnerForm && partnerTeams) {
      // Für Partnerform-Gruppen: Zähle Teams
      return partnerTeams.filter(team => isPartnerTeamChecked(groupId, team)).length;
    }
    // Für normale Gruppen: Zähle einzelne Teilnehmer
    return participants.filter(p => isParticipantChecked(groupId, p.id)).length;
  };

  // Hilfsfunktion: Initialen aus Name generieren
  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // NEUE FUNKTIONEN FÜR OPTIMIERUNG

  // Berechne Zeit bis Gruppenstart
  const getTimeUntilStart = (startTime) => {
    if (!startTime) return null;
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = start.getTime() - now.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 0) return { minutes: 0, label: "Gestartet", color: "slate" };
    if (diffMinutes <= 5) return { minutes: diffMinutes, label: `${diffMinutes}m`, color: "red" };
    if (diffMinutes <= 10) return { minutes: diffMinutes, label: `${diffMinutes}m`, color: "orange" };
    return { minutes: diffMinutes, label: `${diffMinutes}m`, color: "green" };
  };

  // Berechne globale Statistiken
  const getGlobalStats = () => {
    const allEvents = currentEvent
      ? [currentEvent, ...preparationEventsWithParticipants]
      : preparationEventsWithParticipants;

    let totalParticipants = 0;
    let checkedParticipants = 0;
    let completeGroups = 0;

    allEvents.forEach(event => {
      if (!event.group?.id) return;
      const isPartnerForm = event.isPartnerForm;
      const items = isPartnerForm && event.partnerTeams ? event.partnerTeams : event.participants || [];
      const count = items.length;
      const checked = getCheckedCount(event.group.id, event.participants || [], event.partnerTeams, isPartnerForm);

      totalParticipants += count;
      checkedParticipants += checked;
      if (count > 0 && checked === count) completeGroups++;
    });

    return {
      totalGroups: allEvents.length,
      completeGroups,
      totalParticipants,
      checkedParticipants,
      missingParticipants: totalParticipants - checkedParticipants
    };
  };

  // Filtere Teilnehmer nach Suchbegriff und showOnlyMissing
  const filterParticipants = (participants, groupId, isPartnerForm = false) => {
    let filtered = [...participants];

    // Suche anwenden
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => {
        if (isPartnerForm) {
          const name1 = p.participant1?.name?.toLowerCase() || '';
          const name2 = p.participant2?.name?.toLowerCase() || '';
          const num = String(p.startNumber || '');
          return name1.includes(term) || name2.includes(term) || num.includes(term);
        }
        const name = p.name?.toLowerCase() || '';
        const num = String(p.participantNumber || p.id || '');
        const startNum = String(p.groups?.find(g => g.id === groupId)?.startNumber || '');
        return name.includes(term) || num.includes(term) || startNum.includes(term);
      });
    }

    // Nur fehlende anzeigen
    if (showOnlyMissing) {
      filtered = filtered.filter(p => {
        if (isPartnerForm) {
          return !isPartnerTeamChecked(groupId, p);
        }
        return !isParticipantChecked(groupId, p.id);
      });
    }

    // Sortierung: Fehlende zuerst
    filtered.sort((a, b) => {
      const aChecked = isPartnerForm ? isPartnerTeamChecked(groupId, a) : isParticipantChecked(groupId, a.id);
      const bChecked = isPartnerForm ? isPartnerTeamChecked(groupId, b) : isParticipantChecked(groupId, b.id);
      if (aChecked === bChecked) return 0;
      return aChecked ? 1 : -1; // Nicht abgehakte zuerst
    });

    return filtered;
  };

  // "Alle anwesend" Modal öffnen
  const handleMarkAllPresent = (groupId, groupName) => {
    setConfirmAllGroupId(groupId);
    setConfirmAllGroupName(groupName);
    setShowConfirmAllModal(true);
  };

  // "Alle anwesend" bestätigen
  const handleConfirmMarkAll = async () => {
    if (!confirmAllGroupId) return;
    setShowConfirmAllModal(false);

    // Finde die Gruppe
    const event = currentEvent?.group?.id === confirmAllGroupId
      ? currentEvent
      : preparationEventsWithParticipants.find(e => e.group?.id === confirmAllGroupId);

    if (!event) return;

    const isPartnerForm = event.isPartnerForm;
    const items = isPartnerForm && event.partnerTeams ? event.partnerTeams : event.participants || [];

    // Alle nicht-abgehakten abhaken
    for (const item of items) {
      if (isPartnerForm) {
        if (!isPartnerTeamChecked(confirmAllGroupId, item)) {
          await handlePartnerTeamCheckboxChange(confirmAllGroupId, item, true);
        }
      } else {
        if (!isParticipantChecked(confirmAllGroupId, item.id)) {
          await handleCheckboxChange(confirmAllGroupId, item.id, true);
        }
      }
    }

    setConfirmAllGroupId(null);
    setConfirmAllGroupName("");
  };

  // Wenn keine venue ausgewählt ist, zeige Flächenauswahl
  if (!assignedVenueFromUser && !selectedVenue) {
    return (
      <TailwindPageWrapper>
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto">
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("caller.selectVenue.title", "Fläche auswählen")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2 text-sm">
                  {t("caller.selectVenue.subtitle", "Bitte wählen Sie die Fläche aus, für die Sie die Athleten-Anwesenheit verwalten möchten.")}
                </p>
              </div>

              {/* Flächenauswahl */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="space-y-3">
                  <button
                    onClick={() => setSelectedVenue("Fläche A")}
                    className="w-full px-6 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-left hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors"
                  >
                    <div className="font-semibold text-slate-900 dark:text-slate-100">Fläche A</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {t("caller.selectVenue.venueA", "Athleten-Anwesenheit für Fläche A verwalten")}
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedVenue("Fläche B")}
                    className="w-full px-6 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-left hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors"
                  >
                    <div className="font-semibold text-slate-900 dark:text-slate-100">Fläche B</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {t("caller.selectVenue.venueB", "Athleten-Anwesenheit für Fläche B verwalten")}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </TailwindPageWrapper>
    );
  }

  return (
    <TailwindPageWrapper>
      <div className="min-h-screen bg-slate-50/50 dark:bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("caller.title", "Caller - Athleten-Anwesenheit")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-1">
                  {assignedVenue}
                </p>
              </div>
              {/* Flächenauswahl */}
              {!assignedVenueFromUser && (
                <div className="w-48">
                  <GenericSelectTailwind
                    value={selectedVenue || ""}
                    onChange={(value) => setSelectedVenue(value || "")}
                    options={[
                      { value: "Fläche A", label: t("caller.venueA", "Fläche A") },
                      { value: "Fläche B", label: t("caller.venueB", "Fläche B") }
                    ]}
                    placeholder={t("caller.selectVenuePlaceholder", "Fläche wählen...")}
                    whiteBackground={true}
                  />
                </div>
              )}
            </div>
          </div>
          {/* KPI Cards - wie AdminTailwindDashboard */}
          {!loading && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {(() => {
                const stats = getGlobalStats();
                return (
                  <>
                    {/* Gruppen vollständig */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Gruppen
                        </p>
                        <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
                          {stats.completeGroups}/{stats.totalGroups}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                        <i className="fas fa-check-double"></i>
                      </div>
                    </div>

                    {/* Anwesend */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Anwesend
                        </p>
                        <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
                          {stats.checkedParticipants}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                        <i className="fas fa-user-check"></i>
                      </div>
                    </div>

                    {/* Fehlen noch */}
                    <div className={`rounded-2xl border shadow-sm p-5 flex items-center justify-between ${stats.missingParticipants > 0
                      ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                      }`}>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Fehlen noch
                        </p>
                        <p className={`mt-1 text-2xl font-bold ${stats.missingParticipants > 0
                          ? 'text-orange-700 dark:text-orange-300'
                          : 'text-slate-900 dark:text-slate-100'
                          }`}>
                          {stats.missingParticipants}
                        </p>
                      </div>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stats.missingParticipants > 0
                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                        }`}>
                        <i className="fas fa-user-clock"></i>
                      </div>
                    </div>

                    {/* Gesamt */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Gesamt
                        </p>
                        <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
                          {stats.totalParticipants}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-400">
                        <i className="fas fa-users"></i>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Suchfeld und Filter */}
          {!loading && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Suchfeld */}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder={t("caller.searchPlaceholder", "Teilnehmer suchen...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  )}
                </div>
                {/* Filter Toggle */}
                <button
                  onClick={() => setShowOnlyMissing(!showOnlyMissing)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${showOnlyMissing
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                >
                  <i className="fas fa-filter text-xs"></i>
                  {showOnlyMissing ? 'Nur Fehlende' : 'Alle anzeigen'}
                </button>
              </div>
            </div>
          )}

          {/* Bestätigungsmodal für "Alle anwesend" */}
          {showConfirmAllModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-xl max-w-md w-full p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-check-double text-emerald-600 dark:text-emerald-400 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    Alle als anwesend markieren?
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    Alle Teilnehmer in <strong>{confirmAllGroupName}</strong> werden als anwesend markiert.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirmAllModal(false)}
                    className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleConfirmMarkAll}
                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Bestätigen
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">Lade Daten...</p>
            </div>
          ) : (
            <>
              {/* Aktuelles Event */}
              {currentEvent ? (
                <div className="mb-8">
                  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 border-l-4 border-l-emerald-500 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                      {/* Zeile 1: Kategoriename + Zeit + Status-Badge */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-snug">
                            {(() => {
                              const cat = currentEvent.group?.category?.name;
                              const full = currentEvent.group?.name || currentEvent.category || '';
                              return cat || (full.indexOf(' - ') > 0 ? full.substring(0, full.indexOf(' - ')).trim() : full);
                            })()}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            <i className="fas fa-clock text-[9px]"></i>
                            <span className="tabular-nums">{formatTime(currentEvent.startTime)} – {formatTime(currentEvent.endTime)}</span>
                          </div>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-300/50 dark:border-emerald-600/50 flex-shrink-0">
                          <i className="fas fa-play mr-1 text-[9px]"></i>
                          {t("caller.status.active", "Aktiv")}
                        </span>
                      </div>

                      {/* Zeile 2: Progress + Alle anwesend */}
                      {(() => {
                        const checkedCount = getCheckedCount(
                          currentEvent.group.id,
                          currentEvent.participants,
                          currentEvent.partnerTeams,
                          currentEvent.isPartnerForm
                        );
                        const totalCount = currentEvent.isPartnerForm && currentEvent.partnerTeams
                          ? currentEvent.partnerTeams.length
                          : currentEvent.participants.length;
                        const percentage = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;

                        return (
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                              <div
                                className={`h-1.5 rounded-full transition-all duration-500 ${percentage === 100 ? 'bg-emerald-500' : 'bg-primary dark:bg-primary-light'}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-slate-600 dark:text-slate-400 flex-shrink-0 tabular-nums">
                              {checkedCount}/{totalCount}
                            </span>
                            {checkedCount < totalCount && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAllPresent(currentEvent.group.id, currentEvent.group?.name || currentEvent.category);
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-700/50 transition-colors flex-shrink-0"
                              >
                                <i className="fas fa-check-double text-[9px]"></i>
                                Alle anwesend
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="p-5 pt-4">
                      {currentEvent.isPartnerForm && currentEvent.partnerTeams && currentEvent.partnerTeams.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {currentEvent.partnerTeams.map((team) => {
                            const isChecked = isPartnerTeamChecked(currentEvent.group.id, team);
                            const groupChecklist = checklistData.get(currentEvent.group.id);
                            const participant1Item = team.participant1 ? groupChecklist?.get(team.participant1.id) : null;
                            const participant2Item = team.participant2 ? groupChecklist?.get(team.participant2.id) : null;
                            const isLate = participant1Item?.isLate || participant2Item?.isLate || false;

                            const nationCode = team.participant1?.nation?.code || team.participant1?.nationCode || team.participant2?.nation?.code || team.participant2?.nationCode || 'DE';
                            const nationName = team.participant1?.nation?.name || team.participant2?.nation?.name || team.participant1?.nation || team.participant2?.nation || '';
                            const participant1Id = team.participant1?.participantNumber || team.participant1?.id || '-';
                            const participant2Id = team.participant2?.participantNumber || team.participant2?.id || '-';
                            const teamName = `${team.participant1?.name || 'Unbekannt'} & ${team.participant2?.name || 'Unbekannt'}`;

                            return (
                              <div
                                key={team.id}
                                onClick={() => {
                                  // In aktiver Gruppe: Haken für bereits abgehakte Teams nicht wieder entfernen
                                  if (isChecked) return;
                                  handlePartnerTeamCheckboxChange(currentEvent.group.id, team, true);
                                }}
                                className={`rounded-2xl border transition-all duration-200 p-4 ${isChecked
                                  ? isLate
                                    ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-300 dark:border-yellow-700 cursor-not-allowed'
                                    : 'bg-emerald-50/60 dark:bg-emerald-900/10 border-emerald-300 dark:border-emerald-700 cursor-not-allowed'
                                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary/40 dark:hover:border-primary-light/40 hover:shadow-sm cursor-pointer'
                                  }`}
                              >
                                <div className="flex items-center gap-3">
                                  <img
                                    src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                    alt={nationName}
                                    className="w-10 h-10 object-cover rounded-full border-2 border-slate-200 dark:border-slate-600 flex-shrink-0"
                                    onError={(e) => { e.target.src = ''; }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                        {teamName}
                                      </span>
                                      {hasResult(team.id, true) && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50">
                                          <i className="fas fa-flag-checkered text-[8px]"></i>
                                          Fertig
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2.5 mt-0.5 flex-wrap">
                                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                        {nationName}
                                      </span>
                                      <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                                        <i className="fas fa-id-badge text-[9px]"></i>
                                        <span className="tabular-nums font-medium">{participant1Id}</span>
                                        <span className="text-slate-300 dark:text-slate-600">&</span>
                                        <span className="tabular-nums font-medium">{participant2Id}</span>
                                      </span>
                                      <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                                        <i className="fas fa-hashtag text-[9px]"></i>
                                        <span className="tabular-nums font-medium">{team.startNumber || '-'}</span>
                                      </span>
                                    </div>
                                  </div>
                                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${isChecked
                                    ? isLate ? 'bg-yellow-400 border-yellow-400' : 'bg-emerald-500 border-emerald-500'
                                    : 'border-slate-300 dark:border-slate-600'
                                    }`}>
                                    {isChecked && <i className="fas fa-check text-white text-[10px]"></i>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : currentEvent.participants && currentEvent.participants.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {currentEvent.participants.map((participant) => {
                            const participantGroup = participant.groups?.find(g => g.id === currentEvent.group.id);
                            const startNumber = participantGroup?.startNumber || '-';
                            const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                            const isChecked = isParticipantChecked(currentEvent.group.id, participant.id);
                            const groupChecklist = checklistData.get(currentEvent.group.id);
                            const checklistItem = groupChecklist?.get(participant.id);
                            const isLate = checklistItem?.isLate || false;

                            return (
                              <div
                                key={participant.id}
                                onClick={() => {
                                  if (isChecked) return;
                                  handleCheckboxChange(currentEvent.group.id, participant.id, true);
                                }}
                                className={`rounded-2xl border transition-all duration-200 p-4 ${isChecked
                                  ? isLate
                                    ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-300 dark:border-yellow-700 cursor-not-allowed'
                                    : 'bg-emerald-50/60 dark:bg-emerald-900/10 border-emerald-300 dark:border-emerald-700 cursor-not-allowed'
                                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary/40 dark:hover:border-primary-light/40 hover:shadow-sm cursor-pointer'
                                  }`}
                              >
                                <div className="flex items-center gap-3">
                                  <img
                                    src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                    alt={participant.nation?.name || participant.nation}
                                    className="w-10 h-10 object-cover rounded-full border-2 border-slate-200 dark:border-slate-600 flex-shrink-0"
                                    onError={(e) => { e.target.src = ''; }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                        {participant.name}
                                      </span>
                                      {hasResult(participant.id, false) && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50">
                                          <i className="fas fa-flag-checkered text-[8px]"></i>
                                          Fertig
                                        </span>
                                      )}
                                      {isLate && isChecked && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-700/50">
                                          <i className="fas fa-clock text-[8px]"></i>
                                          Nachzügler
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2.5 mt-0.5 flex-wrap">
                                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                        {participant.nation?.name || participant.nation}
                                      </span>
                                      <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                                        <i className="fas fa-id-badge text-[9px]"></i>
                                        <span className="tabular-nums font-medium">{participant.participantNumber || participant.id}</span>
                                      </span>
                                      <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                                        <i className="fas fa-hashtag text-[9px]"></i>
                                        <span className="tabular-nums font-medium">{startNumber}</span>
                                      </span>
                                    </div>
                                  </div>
                                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${isChecked
                                    ? isLate ? 'bg-yellow-400 border-yellow-400' : 'bg-emerald-500 border-emerald-500'
                                    : 'border-slate-300 dark:border-slate-600'
                                    }`}>
                                    {isChecked && <i className="fas fa-check text-white text-[10px]"></i>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500 dark:text-slate-400 text-sm">
                          {t("caller.currentEventInfo", "Diese Gruppe läuft derzeit. Die nächsten Gruppen sind in Vorbereitung.")}
                        </div>
                      )}


                    </div>
                    {((currentEvent.isPartnerForm && currentEvent.partnerTeams && currentEvent.partnerTeams.length > 0) ||
                      (currentEvent.participants && currentEvent.participants.length > 0)) && (
                        <div className="border-t border-slate-100 dark:border-slate-700/60 px-5 py-3">
                          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                            <i className="fas fa-info-circle text-[10px]"></i>
                            {t("caller.lateParticipants", "Nachzügler können nachträglich abgehakt werden.")}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              ) : (
                <div className="mb-8 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-12">
                  <div className="text-center">
                    <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-clock text-slate-400 dark:text-slate-500 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                      {t("caller.noActiveEvent", "Kein aktives Event")}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                      {t("caller.noActiveEventDesc", "Es läuft derzeit kein Event auf dieser Fläche")}
                    </p>
                  </div>
                </div>
              )}

              {/* Gruppen in Vorbereitung */}
              {preparationEventsWithParticipants.length > 0 ? (
                <div className="mb-8">
                  <div className="flex items-center gap-2.5 mb-4">
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                      <i className="fas fa-hourglass-half text-[10px]"></i>
                      {t("caller.preparationGroups", "Gruppen in Vorbereitung")}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-700/50">
                      {preparationEventsWithParticipants.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {preparationEventsWithParticipants.map((event) => {
                      const checkedCount = getCheckedCount(event.group.id, event.participants, event.partnerTeams, event.isPartnerForm);
                      const totalCount = event.isPartnerForm && event.partnerTeams ? event.partnerTeams.length : event.participants.length;

                      return (
                        <div
                          key={event.id}
                          className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 border-l-4 border-l-orange-400 shadow-sm overflow-hidden"
                        >
                          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                            {/* Zeile 1: Kategoriename + Zeit + Status-Badges */}
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug">
                                  {(() => {
                                    const cat = event.group?.category?.name;
                                    const full = event.group?.name || event.category || '';
                                    return cat || (full.indexOf(' - ') > 0 ? full.substring(0, full.indexOf(' - ')).trim() : full);
                                  })()}
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                  <i className="fas fa-clock text-[9px]"></i>
                                  <span className="tabular-nums">{formatTime(event.startTime)} – {formatTime(event.endTime)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {(() => {
                                  const timeInfo = getTimeUntilStart(event.startTime);
                                  if (!timeInfo) return null;
                                  const colors = {
                                    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-300/50 dark:border-red-600/50',
                                    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-300/50 dark:border-orange-600/50',
                                    green: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-300/50 dark:border-emerald-600/50',
                                    slate: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-300/50 dark:border-slate-600/50'
                                  };
                                  return (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${colors[timeInfo.color]}`}>
                                      <i className="fas fa-clock mr-1 text-[9px]"></i>
                                      {timeInfo.label}
                                    </span>
                                  );
                                })()}
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-300/50 dark:border-orange-600/50">
                                  <i className="fas fa-hourglass-half mr-1 text-[9px]"></i>
                                  {t("caller.status.preparation", "Vorbereitung")}
                                </span>
                              </div>
                            </div>

                            {/* Zeile 2: Progress + Alle anwesend */}
                            {(() => {
                              const percentage = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;
                              return (
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                    <div
                                      className={`h-1.5 rounded-full transition-all duration-500 ${percentage === 100 ? 'bg-emerald-500' : 'bg-primary dark:bg-primary-light'}`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400 flex-shrink-0 tabular-nums">
                                    {checkedCount}/{totalCount}
                                  </span>
                                  {checkedCount < totalCount && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkAllPresent(event.group.id, event.group?.name || event.category);
                                      }}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-700/50 transition-colors flex-shrink-0"
                                    >
                                      <i className="fas fa-check-double text-[9px]"></i>
                                      Alle anwesend
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>

                          <div className="p-4">
                            {event.isPartnerForm && event.partnerTeams && event.partnerTeams.length > 0 ? (
                              <div className="space-y-2">
                                {filterParticipants(event.partnerTeams, event.group.id, true).map((team) => {
                                  const isChecked = isPartnerTeamChecked(event.group.id, team);
                                  const nationCode = team.participant1?.nation?.code || team.participant1?.nationCode || team.participant2?.nation?.code || team.participant2?.nationCode || 'DE';
                                  const nationName = team.participant1?.nation?.name || team.participant2?.nation?.name || team.participant1?.nation || team.participant2?.nation || '';
                                  const teamName = `${team.participant1?.name || 'Unbekannt'} & ${team.participant2?.name || 'Unbekannt'}`;

                                  return (
                                    <div
                                      key={team.id}
                                      onClick={() => handlePartnerTeamCheckboxChange(event.group.id, team, !isChecked)}
                                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-200 cursor-pointer ${isChecked
                                        ? 'bg-emerald-50/60 dark:bg-emerald-900/10 border-emerald-300 dark:border-emerald-700'
                                        : 'bg-slate-50 dark:bg-slate-700/30 border-slate-200 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                      <img
                                        src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                        alt={nationName}
                                        className="w-7 h-7 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                                        onError={(e) => { e.target.src = ''; }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">
                                          {teamName}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                          <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{nationName}</span>
                                          <span className="flex items-center gap-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                                            <i className="fas fa-hashtag text-[8px]"></i>
                                            <span className="tabular-nums">{team.startNumber || '-'}</span>
                                          </span>
                                        </div>
                                      </div>
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${isChecked
                                        ? 'bg-emerald-500 border-emerald-500'
                                        : 'border-slate-300 dark:border-slate-600'
                                        }`}>
                                        {isChecked && <i className="fas fa-check text-white text-[9px]"></i>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : event.participants.length > 0 ? (
                              <div className="space-y-2">
                                {filterParticipants(event.participants, event.group.id, false).map((participant) => {
                                  const participantGroup = participant.groups?.find(g => g.id === event.group.id);
                                  const startNumber = participantGroup?.startNumber || '-';
                                  const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                                  const isChecked = isParticipantChecked(event.group.id, participant.id);

                                  return (
                                    <div
                                      key={participant.id}
                                      onClick={() => handleCheckboxChange(event.group.id, participant.id, !isChecked)}
                                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-200 cursor-pointer ${isChecked
                                        ? 'bg-emerald-50/60 dark:bg-emerald-900/10 border-emerald-300 dark:border-emerald-700'
                                        : 'bg-slate-50 dark:bg-slate-700/30 border-slate-200 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                      <img
                                        src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                        alt={participant.nation?.name || participant.nation}
                                        className="w-7 h-7 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                                        onError={(e) => { e.target.src = ''; }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">
                                          {participant.name}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                          <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{participant.nation?.name || participant.nation}</span>
                                          <span className="flex items-center gap-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                                            <i className="fas fa-id-badge text-[8px]"></i>
                                            <span className="tabular-nums">{participant.participantNumber || participant.id}</span>
                                          </span>
                                          <span className="flex items-center gap-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                                            <i className="fas fa-hashtag text-[8px]"></i>
                                            <span className="tabular-nums">{startNumber}</span>
                                          </span>
                                        </div>
                                      </div>
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${isChecked
                                        ? 'bg-emerald-500 border-emerald-500'
                                        : 'border-slate-300 dark:border-slate-600'
                                        }`}>
                                        {isChecked && <i className="fas fa-check text-white text-[9px]"></i>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-slate-500 dark:text-slate-400 text-sm">
                                {t("caller.noParticipants", "Keine Teilnehmer für diese Gruppe")}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-12">
                  <div className="text-center">
                    <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-hourglass-half text-slate-400 dark:text-slate-500 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                      {t("caller.noPreparationGroups", "Keine Gruppen in Vorbereitung")}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                      {t("caller.noPreparationGroupsDesc", "Derzeit sind keine Gruppen in Vorbereitung")}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </TailwindPageWrapper>
  );
};

export default CallerView;
